// grain — deterministic per-pixel monochrome noise. Amplitude scales with the
// palette's `--grain-opacity` so dark palettes (midnight, faded) get more grain
// to match the design canon CSS.
//
// Determinism comes from c.rng (seededRandom(photoId)). Same {photoId,palette}
// → identical noise pattern, which is what makes the visual goldens stable.

import {
  type FilterContext,
  GRAIN_OPACITY_BY_PALETTE,
  eachPixel,
  getImageData,
  putImageData,
} from './_helpers'

// Bumped 30 → 44 → 56 over iterations. F-wave β wants visible film grain at
// 1:1 zoom on the Polaroid wrapper — combined with the always-on bloom step
// the grain reads as "actual emulsion" instead of a faint texture.
// Palette opacity multipliers (0.18 / 0.28 / 0.36) stay tied to the canon
// CSS values — see GRAIN_OPACITY_BY_PALETTE in _helpers.ts.
const NOISE_AMPLITUDE = 56

export function grain(c: FilterContext): void {
  const opacity = GRAIN_OPACITY_BY_PALETTE[c.palette]
  const amp = NOISE_AMPLITUDE * opacity
  const img = getImageData(c.ctx)
  eachPixel(img, (r, g, b, a) => {
    // Single noise sample per pixel — monochrome grain, applied to all channels.
    // Three independent samples would chroma-shift the image (rainbow grain).
    const delta = (c.rng() - 0.5) * amp
    return [r + delta, g + delta, b + delta, a]
  })
  putImageData(c.ctx, img)
}
